import { useState, useEffect, useCallback } from 'react';

type EmbedTheme = 'dark' | 'light';

/**
 * Hook to detect and manage embed/iframe mode
 * 
 * Embed mode is triggered by:
 * 1. Being inside an iframe
 * 2. ?embed=true query parameter (useful for testing)
 * 
 * Theme can be set via:
 * - ?theme=light or ?theme=dark (default is dark)
 * 
 * Transparent mode can be enabled via:
 * - ?transparent=true (removes all solid backgrounds)
 * 
 * Minimal mode can be enabled via:
 * - ?minimal=true (hides logo, title, description - shows only calendar/form)
 * 
 * In embed mode:
 * - Header/branding is hidden
 * - Tighter spacing is used
 * - Height is communicated to parent for seamless resizing
 */
export function useEmbedMode() {
    const [isEmbedded, setIsEmbedded] = useState(false);
    const [theme, setTheme] = useState<EmbedTheme>('dark');
    const [isTransparent, setIsTransparent] = useState(false);
    const [isMinimal, setIsMinimal] = useState(false);

    useEffect(() => {
        const inIframe = window.self !== window.top;
        
        const urlParams = new URLSearchParams(window.location.search);
        const embedParam = urlParams.get('embed') === 'true';
        const themeParam = urlParams.get('theme') as EmbedTheme | null;
        const transparentParam = urlParams.get('transparent') === 'true';
        const minimalParam = urlParams.get('minimal') === 'true';

        setIsEmbedded(inIframe || embedParam);
        setIsTransparent(transparentParam);
        setIsMinimal(minimalParam);
        
        if (themeParam === 'light') {
            setTheme('light');
        }

        if (inIframe || embedParam) {
            document.body.classList.add('embed-mode');
        } else {
            document.body.classList.remove('embed-mode');
        }

        if (themeParam === 'light') {
            document.body.classList.add('theme-light');
            document.body.classList.remove('theme-dark');
        } else {
            document.body.classList.add('theme-dark');
            document.body.classList.remove('theme-light');
        }

        if (transparentParam) {
            document.body.classList.add('transparent-mode');
        } else {
            document.body.classList.remove('transparent-mode');
        }

        if (minimalParam) {
            document.body.classList.add('minimal-mode');
        } else {
            document.body.classList.remove('minimal-mode');
        }

        return () => {
            document.body.classList.remove('embed-mode');
            document.body.classList.remove('theme-light');
            document.body.classList.remove('theme-dark');
            document.body.classList.remove('transparent-mode');
            document.body.classList.remove('minimal-mode');
        };
    }, []);

    const notifyParentHeight = useCallback(() => {
        if (!isEmbedded) return;

        try {
            const height = document.documentElement.scrollHeight;
            window.parent.postMessage({ type: 'letsmeet-resize', height }, '*');
        } catch {
        }
    }, [isEmbedded]);

    useEffect(() => {
        if (!isEmbedded) return;

        notifyParentHeight();

        const observer = new ResizeObserver(() => {
            notifyParentHeight();
        });

        observer.observe(document.body);

        window.addEventListener('resize', notifyParentHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', notifyParentHeight);
        };
    }, [isEmbedded, notifyParentHeight]);

    return { isEmbedded, theme, isTransparent, isMinimal, notifyParentHeight };
}
